import { chakraUseBreakpointValue } from "@airhelp/react-ui-components";
import type { LanguageCode } from "config/languages";
import {
  companyMenu,
  imprintMenu,
  knowYourRightsMenu,
  legalMenu,
  productsMenu,
  supportMenu,
  topMenu,
} from "config/menus";
import type { MenuExtendedProps } from "config/menus";
import {
  AIR_PAYOUT_ID,
  IMPRINT_ID,
  OUR_FEES_ID,
  OVERBOOKING_COMPENSATION_ID,
  PRICE_LIST_ID,
} from "config/menus/menuPageId";
import { useServerSideStoryblokPages } from "contexts/DataContext/hooks";
import { getParentLanguage } from "utils/localeUtils";
import type { ResolvedMenuPagePaths } from "utils/menuUtils";
import { filterMenuByExcludedId, resolveMenuPagePaths } from "utils/menuUtils/menuUtils";

export const useResolveMenuPagePaths = (language: LanguageCode, menu: MenuExtendedProps[]): ResolvedMenuPagePaths[] => {
  const serverSideStoryblokPages = useServerSideStoryblokPages();
  const parentLanguage = getParentLanguage(language);

  return resolveMenuPagePaths(menu, language, parentLanguage, serverSideStoryblokPages);
};

const NON_OVERBOOKING_PAGE_IN_MENU_LANGUAGES: LanguageCode[] = [
  "pt-pt",
  "pt-br",
  "ro",
  "ro-md",
  "sv",
  "fi",
  "nb",
  "ru",
  "el",
];

export const useKnowYourRightsMenu = (language: LanguageCode) => {
  let menu = knowYourRightsMenu;
  menu = NON_OVERBOOKING_PAGE_IN_MENU_LANGUAGES.includes(language)
    ? filterMenuByExcludedId(menu, OVERBOOKING_COMPENSATION_ID)
    : menu;

  const notSupportedLanguages: LanguageCode[] = ["el", "ru"];

  menu = notSupportedLanguages.includes(language) ? menu.filter(({ page }) => page !== "airports") : menu;

  return useResolveMenuPagePaths(language, menu);
};

export const useImprintMenu = (language: LanguageCode) => {
  let menu = imprintMenu;
  const supportedLanguages: LanguageCode[] = ["en", "de"];
  menu = supportedLanguages.includes(getParentLanguage(language)) ? menu : filterMenuByExcludedId(menu, IMPRINT_ID);

  return useResolveMenuPagePaths(language, menu);
};

export const useLegalMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, legalMenu);

export const useProductsMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, productsMenu);

export const useCompanyMenu = (language: LanguageCode) => useResolveMenuPagePaths(language, companyMenu);

export const useSupportMenu = (language: LanguageCode) => {
  const notTranslatedOurFeesPages: LanguageCode[] = ["pt-br"];

  let menu = supportMenu;

  menu = notTranslatedOurFeesPages.includes(language)
    ? filterMenuByExcludedId(menu, OUR_FEES_ID)
    : filterMenuByExcludedId(menu, PRICE_LIST_ID);

  return useResolveMenuPagePaths(language, menu);
};

export const useTopMenu = (language: LanguageCode) => {
  // // TODO | Remove after a new desing is ready
  // // Hide the AirPayout link in top menu for for desktop (temporary solution, reason - it does not fit :)
  const isDesktop = chakraUseBreakpointValue({ lg: true }, { fallback: "lg" });
  const topMenuFiltered = isDesktop ? filterMenuByExcludedId(topMenu, AIR_PAYOUT_ID) : topMenu;

  const menu = topMenuFiltered;
  const knowYourRightsMenuMapped = useKnowYourRightsMenu(language);

  menu.map((item) => {
    const menuItem = item;
    if (menuItem.subMenu) menuItem.subMenu = knowYourRightsMenuMapped;

    return menuItem;
  });

  return useResolveMenuPagePaths(language, menu);
};
